/**
 * What a score card says about its game (console spec §5.5), kept pure so the
 * Scores page's cards can be tested as data: the two sides by name or
 * placeholder, the context line, and whether a later bracket game has already
 * been played off this one's result.
 */
import type { ResolvedBracketGame } from "@alpha/engine";
import { slotLabel, teamName } from "./bundle";
import type { Bundle, Game } from "./types";

export interface Side {
  /** Null until the side is known — a TBD pool slot, or a bracket slot still waiting on seeds or a result. */
  teamId: string | null;
  /** The team's name, or the engine's own wording for an unknown side: "TBD", "Seed 3", "Winner Gm 4". */
  label: string;
}

export interface GameSides {
  home: Side;
  away: Side;
  /** "9U · Pool A" or "9U · Gm 3": what tells a pool game from a bracket game on a mixed day. */
  context: string;
  /** Either side unknown, so there is nothing to score yet. */
  unresolved: boolean;
}

/** `resolved` is the bracket resolver's view of a bracket game; a pool game has none. */
export function gameSides(bundle: Bundle, game: Game, resolved?: ResolvedBracketGame): GameSides {
  const division = bundle.divisions.find((d) => d.id === game.divisionId)?.name ?? "";
  if (game.type === "bracket") {
    const side = (slot: ResolvedBracketGame["home"] | undefined): Side =>
      slot ? { teamId: slot.teamId, label: slotLabel(bundle, slot) } : { teamId: null, label: "TBD" };
    const home = side(resolved?.home);
    const away = side(resolved?.away);
    return {
      home,
      away,
      context: [division, resolved ? `Gm ${resolved.gameNumber}` : "Bracket"].filter(Boolean).join(" · "),
      unresolved: home.teamId === null || away.teamId === null,
    };
  }
  const pool = bundle.pools.find((p) => p.id === game.poolId)?.name ?? "";
  const side = (teamId: string | undefined): Side => ({
    teamId: teamId ?? null,
    label: teamId ? teamName(bundle, teamId) : "TBD",
  });
  return {
    home: side(game.homeTeamId),
    away: side(game.awayTeamId),
    context: [division, pool].filter(Boolean).join(" · "),
    unresolved: !game.homeTeamId || !game.awayTeamId,
  };
}

/** `Game.bracket`'s sources are typed `unknown` on the web; this is the one shape the card acts on. */
function isWinnerOf(source: unknown): source is { kind: "winnerOf"; gameId: string } {
  return (
    typeof source === "object" &&
    source !== null &&
    (source as { kind?: unknown }).kind === "winnerOf" &&
    typeof (source as { gameId?: unknown }).gameId === "string"
  );
}

const isFinished = (g: Game): boolean => g.status === "final" || g.status === "forfeit";

/**
 * The display number of a finished bracket game fed by this one's winner, or
 * null. Changing a result under a played downstream game would have the
 * resolver hand that game's score to a different team, so the card offers
 * nothing but the way out (delete the bracket) when this is set.
 *
 * Numbered as the engine numbers them — by round, then position — so the text
 * names the same "Gm 5" the page shows.
 */
export function downstreamFinishedGame(bundle: Bundle, game: Game): number | null {
  if (game.type !== "bracket") return null;
  const bracket = bundle.games
    .filter((g) => g.divisionId === game.divisionId && g.type === "bracket" && g.bracket)
    .sort((a, b) => a.bracket!.round - b.bracket!.round || a.bracket!.position - b.bracket!.position);
  const index = bracket.findIndex(
    (g) =>
      isFinished(g) &&
      ((isWinnerOf(g.bracket!.homeSource) && g.bracket!.homeSource.gameId === game.id) ||
        (isWinnerOf(g.bracket!.awaySource) && g.bracket!.awaySource.gameId === game.id)),
  );
  return index === -1 ? null : index + 1;
}

/** "Hooks forfeited · 7–0 forfeit score": no score is stored on a forfeit, so the card says what counts. */
export function forfeitLine(bundle: Bundle, game: Game): string {
  const { winner, loser } = bundle.tournament.config.forfeitScore;
  const who = game.forfeitTeamId ? teamName(bundle, game.forfeitTeamId) : "A team";
  return `${who} forfeited · ${winner}–${loser} forfeit score`;
}
